import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import DesignServicesRoundedIcon from "@mui/icons-material/DesignServicesRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import HandshakeRoundedIcon from "@mui/icons-material/HandshakeRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import LocalAtmRoundedIcon from "@mui/icons-material/LocalAtmRounded";
import PointOfSaleRoundedIcon from "@mui/icons-material/PointOfSaleRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import SellRoundedIcon from "@mui/icons-material/SellRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import ShoppingCartCheckoutRoundedIcon from "@mui/icons-material/ShoppingCartCheckoutRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../context/AuthContext";

const BRAND = {
  orange: "#F35A3A",
  orangeDark: "#DF4728",
  dark: "#19242F",
  green: "#2A9D67",
  soft: "#F7F8FA",
};

const WHATSAPP_URL = "https://wa.me/message/HOYXZTTFNXNZH1";

const sections = [
  { id: "inicio", label: "Puesta en marcha", icon: RocketLaunchRoundedIcon },
  { id: "pagina", label: "Diseñar la página", icon: DesignServicesRoundedIcon },
  { id: "catalogo", label: "Productos y servicios", icon: Inventory2RoundedIcon },
  { id: "ventas", label: "Pedidos y cobros", icon: ShoppingCartCheckoutRoundedIcon },
  { id: "agenda", label: "Turnos y agenda", icon: CalendarMonthRoundedIcon },
  { id: "gestion", label: "Sistema de gestión", icon: PointOfSaleRoundedIcon },
  { id: "equipo", label: "Equipo y accesos", icon: GroupsRoundedIcon },
  { id: "mensualidad", label: "Plan y mensualidad", icon: LocalAtmRoundedIcon },
  { id: "afiliados", label: "Programa de referidos", icon: HandshakeRoundedIcon },
  { id: "vendedores", label: "Proceso del vendedor", icon: SellRoundedIcon },
  { id: "control", label: "Control y diagnóstico", icon: CheckCircleRoundedIcon },
  { id: "preguntas", label: "Preguntas frecuentes", icon: DescriptionRoundedIcon },
];

const launchSteps = [
  {
    number: "01",
    title: "Crear y verificar la cuenta",
    description:
      "Registrar nombre, correo y contraseña; luego validar el correo para habilitar el acceso normal al panel.",
  },
  {
    number: "02",
    title: "Crear el proyecto",
    description:
      "Definir el nombre del negocio y una dirección pública clara. El sistema prepara el espacio independiente del cliente.",
  },
  {
    number: "03",
    title: "Completar la identidad",
    description:
      "Cargar logo, datos comerciales, colores, textos principales, WhatsApp, horarios y redes sociales.",
  },
  {
    number: "04",
    title: "Configurar la oferta",
    description:
      "Publicar productos o servicios con categorías, precios, imágenes, disponibilidad y condiciones de compra o reserva.",
  },
  {
    number: "05",
    title: "Activar la operación",
    description:
      "Preparar pedidos, cobros, agenda, notificaciones, equipo y sistema de gestión según los módulos contratados.",
  },
  {
    number: "06",
    title: "Probar y publicar",
    description:
      "Revisar la página desde móvil y computadora, completar una operación de prueba y recién después compartir el enlace.",
  },
];

const faqs = [
  {
    question: "¿Necesito saber programación?",
    answer:
      "No. Los pasos técnicos de esta guía son operativos: indican qué configurar, en qué orden, qué resultado esperar y cómo comprobarlo desde el panel.",
  },
  {
    question: "¿Qué debo completar antes de publicar?",
    answer:
      "Como mínimo: identidad del negocio, datos de contacto, página de inicio, una oferta activa y una prueba desde el enlace público. Si habrá ventas o reservas, también deben probarse esos circuitos.",
  },
  {
    question: "¿Los productos y servicios se cargan en el mismo lugar?",
    answer:
      "Sí. Al elegir el tipo de publicación, el panel adapta los campos. Un producto puede controlar stock; un servicio puede definir duración, horarios, capacidad, reservas y forma de pago.",
  },
  {
    question: "¿Por qué una herramienta puede no aparecer?",
    answer:
      "Puede faltar el módulo correspondiente, el proyecto puede estar en un modo de acceso limitado o todavía puede faltar una configuración obligatoria. Revisá Suscripción y luego la configuración del módulo.",
  },
  {
    question: "¿Cuándo se descuenta o confirma el stock?",
    answer:
      "El sistema puede reservar stock durante el proceso del pedido. Cuando el pago queda aprobado, la reserva se confirma; si el pedido vence, se rechaza o se cancela, el stock debe liberarse.",
  },
  {
    question: "¿Cómo se evita superponer turnos?",
    answer:
      "Cada servicio utiliza duración, tiempo previo, tiempo posterior, capacidad, reglas semanales y bloqueos de calendario. La agenda calcula el período realmente ocupado antes de ofrecer un horario.",
  },
  {
    question: "¿Cambiar de plan elimina información?",
    answer:
      "No debería eliminar la información cargada. Puede cambiar qué herramientas se pueden utilizar. Antes de reducir módulos, conviene exportar o revisar la información operativa asociada.",
  },
  {
    question: "¿Cómo funciona el programa de referidos?",
    answer:
      "El origen queda asociado al registrarse mediante un enlace personal. Las comisiones se calculan cuando existe una mensualidad válida y aprobada, usando la política comercial vigente para esa fecha.",
  },
];

function scrollToSection(id) {
  document.getElementById(id)?.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });
}

function SectionHeading({ eyebrow, title, description }) {
  return (
    <Box sx={{ mb: 3.5 }}>
      <Typography
        sx={{
          color: BRAND.orange,
          fontWeight: 950,
          letterSpacing: ".1em",
          textTransform: "uppercase",
          fontSize: ".75rem",
          mb: 1,
        }}
      >
        {eyebrow}
      </Typography>
      <Typography
        variant="h3"
        sx={{
          color: BRAND.dark,
          fontWeight: 950,
          letterSpacing: "-.04em",
          lineHeight: 1.08,
          fontSize: { xs: "2rem", md: "2.65rem" },
        }}
      >
        {title}
      </Typography>
      {description ? (
        <Typography
          sx={{
            mt: 1.5,
            maxWidth: 900,
            color: "rgba(25,36,47,.66)",
            fontSize: { xs: ".98rem", md: "1.05rem" },
            lineHeight: 1.75,
          }}
        >
          {description}
        </Typography>
      ) : null}
    </Box>
  );
}

function FeatureCard({ icon: Icon, title, description, items = [], badge }) {
  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        borderRadius: 4,
        borderColor: "rgba(25,36,47,.1)",
        boxShadow: "0 18px 45px rgba(25,36,47,.06)",
      }}
    >
      <CardContent
        sx={{
          p: { xs: 2.5, md: 3 },
          "&:last-child": { pb: { xs: 2.5, md: 3 } },
        }}
      >
        <Stack direction="row" spacing={1.4} alignItems="flex-start">
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: 3,
              display: "grid",
              placeItems: "center",
              bgcolor: "rgba(243,90,58,.1)",
              color: BRAND.orange,
              flexShrink: 0,
            }}
          >
            <Icon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            {badge ? (
              <Chip
                label={badge}
                size="small"
                sx={{
                  mb: 1,
                  bgcolor: "#FFF2ED",
                  color: "#B83C23",
                  fontWeight: 850,
                }}
              />
            ) : null}
            <Typography sx={{ color: BRAND.dark, fontWeight: 950, fontSize: "1.08rem" }}>
              {title}
            </Typography>
          </Box>
        </Stack>

        <Typography sx={{ mt: 1.5, color: "rgba(25,36,47,.64)", lineHeight: 1.65 }}>
          {description}
        </Typography>

        {items.length ? (
          <List dense disablePadding sx={{ mt: 1.5 }}>
            {items.map((item) => (
              <ListItem key={item} disableGutters sx={{ alignItems: "flex-start", py: 0.45 }}>
                <ListItemIcon sx={{ minWidth: 28, pt: 0.25 }}>
                  <CheckCircleRoundedIcon sx={{ fontSize: 18, color: BRAND.green }} />
                </ListItemIcon>
                <ListItemText
                  primary={item}
                  primaryTypographyProps={{
                    sx: {
                      color: "rgba(25,36,47,.72)",
                      lineHeight: 1.5,
                      fontSize: ".92rem",
                    },
                  }}
                />
              </ListItem>
            ))}
          </List>
        ) : null}
      </CardContent>
    </Card>
  );
}

function ScreenshotSlot({ children }) {
  return (
    <Box
      sx={{
        mt: 2.5,
        minHeight: 100,
        borderRadius: 3,
        border: "1px dashed rgba(25,36,47,.24)",
        bgcolor: "rgba(25,36,47,.025)",
        display: "grid",
        placeItems: "center",
        p: 2,
        textAlign: "center",
      }}
    >
      <Typography sx={{ color: "rgba(25,36,47,.52)", fontWeight: 800, fontSize: ".84rem" }}>
        Captura sugerida: {children}
      </Typography>
    </Box>
  );
}

function StatusFlow({ items }) {
  return (
    <Stack direction="row" flexWrap="wrap" gap={0.8} sx={{ mt: 1.5 }}>
      {items.map((item, index) => (
        <React.Fragment key={`${item}-${index}`}>
          <Chip
            label={item}
            size="small"
            sx={{ bgcolor: "#fff", border: "1px solid rgba(25,36,47,.12)", fontWeight: 850 }}
          />
          {index < items.length - 1 ? (
            <Typography sx={{ color: "rgba(25,36,47,.3)", alignSelf: "center", fontWeight: 900 }}>
              →
            </Typography>
          ) : null}
        </React.Fragment>
      ))}
    </Stack>
  );
}

function TechnicalGuide({
  title,
  summary,
  prerequisites = [],
  steps = [],
  verification = [],
  warnings = [],
  statusFlow = [],
  screenshot,
  defaultExpanded = false,
}) {
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      elevation={0}
      sx={{
        mb: 1.5,
        border: "1px solid rgba(25,36,47,.1)",
        borderRadius: "18px !important",
        overflow: "hidden",
        bgcolor: "#fff",
        "&::before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreRoundedIcon />}
        sx={{ px: { xs: 2, md: 2.8 }, py: 0.7, "& .MuiAccordionSummary-content": { my: 1.1 } }}
      >
        <Box>
          <Typography sx={{ color: BRAND.dark, fontWeight: 950, fontSize: "1.08rem" }}>
            {title}
          </Typography>
          <Typography sx={{ mt: 0.35, color: "rgba(25,36,47,.58)", lineHeight: 1.55, fontSize: ".9rem" }}>
            {summary}
          </Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ px: { xs: 2, md: 2.8 }, pt: 0, pb: 3 }}>
        {prerequisites.length ? (
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{ color: BRAND.dark, fontWeight: 950, fontSize: ".86rem", textTransform: "uppercase", letterSpacing: ".06em" }}>
              Antes de empezar
            </Typography>
            <List dense disablePadding sx={{ mt: 0.8 }}>
              {prerequisites.map((item) => (
                <ListItem key={item} disableGutters sx={{ alignItems: "flex-start", py: 0.35 }}>
                  <ListItemIcon sx={{ minWidth: 27, pt: 0.2 }}>
                    <CheckCircleRoundedIcon sx={{ fontSize: 17, color: BRAND.green }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={item}
                    primaryTypographyProps={{ sx: { color: "rgba(25,36,47,.7)", lineHeight: 1.5, fontSize: ".9rem" } }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        ) : null}

        <Stack spacing={1.4}>
          {steps.map((step, index) => (
            <Paper
              key={`${step.title}-${index}`}
              elevation={0}
              sx={{
                p: { xs: 1.8, md: 2.2 },
                borderRadius: 3,
                border: "1px solid rgba(25,36,47,.08)",
                bgcolor: index % 2 === 0 ? "#FAFBFC" : "#fff",
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box
                  sx={{
                    width: 31,
                    height: 31,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    bgcolor: "rgba(243,90,58,.1)",
                    color: BRAND.orangeDark,
                    fontWeight: 950,
                    flexShrink: 0,
                    fontSize: ".8rem",
                  }}
                >
                  {index + 1}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ color: BRAND.dark, fontWeight: 950 }}>{step.title}</Typography>
                  <Typography sx={{ mt: 0.55, color: "rgba(25,36,47,.7)", lineHeight: 1.62, fontSize: ".92rem" }}>
                    {step.action}
                  </Typography>
                  {step.result ? (
                    <Typography sx={{ mt: 0.7, color: "#236746", lineHeight: 1.55, fontSize: ".86rem", fontWeight: 800 }}>
                      Resultado esperado: {step.result}
                    </Typography>
                  ) : null}
                  {step.note ? (
                    <Typography sx={{ mt: 0.55, color: "rgba(25,36,47,.52)", lineHeight: 1.55, fontSize: ".83rem" }}>
                      {step.note}
                    </Typography>
                  ) : null}
                </Box>
              </Stack>
            </Paper>
          ))}
        </Stack>

        {statusFlow.length ? (
          <Box sx={{ mt: 2.5 }}>
            <Typography sx={{ color: BRAND.dark, fontWeight: 950, fontSize: ".86rem", textTransform: "uppercase", letterSpacing: ".06em" }}>
              Flujo habitual de estados
            </Typography>
            <StatusFlow items={statusFlow} />
          </Box>
        ) : null}

        {verification.length ? (
          <Box sx={{ mt: 2.5, p: 2.2, borderRadius: 3, bgcolor: "#F2F8F5", border: "1px solid rgba(42,157,103,.15)" }}>
            <Typography sx={{ color: "#205F42", fontWeight: 950 }}>Cómo comprobar que quedó bien</Typography>
            <List dense disablePadding sx={{ mt: 0.7 }}>
              {verification.map((item) => (
                <ListItem key={item} disableGutters sx={{ py: 0.3, alignItems: "flex-start" }}>
                  <ListItemIcon sx={{ minWidth: 27, pt: 0.2 }}>
                    <CheckCircleRoundedIcon sx={{ fontSize: 17, color: BRAND.green }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={item}
                    primaryTypographyProps={{ sx: { color: "rgba(25,73,51,.77)", lineHeight: 1.5, fontSize: ".88rem" } }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        ) : null}

        {warnings.map((warning) => (
          <Alert key={warning} severity="warning" sx={{ mt: 1.5, borderRadius: 3 }}>
            {warning}
          </Alert>
        ))}

        {screenshot ? <ScreenshotSlot>{screenshot}</ScreenshotSlot> : null}
      </AccordionDetails>
    </Accordion>
  );
}

function DocumentationSection({ id, children, shaded = false }) {
  return (
    <Box
      component="section"
      id={id}
      sx={{
        scrollMarginTop: 105,
        py: { xs: 6, md: 9 },
        px: { xs: 2, md: 4 },
        bgcolor: shaded ? BRAND.soft : "#fff",
        borderTop: "1px solid rgba(25,36,47,.06)",
      }}
    >
      <Container maxWidth="lg" disableGutters>
        {children}
      </Container>
    </Box>
  );
}

export default function DocumentationPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const isAuthenticated = Boolean(user);

  React.useEffect(() => {
    document.title = "Documentación | WebProfit";
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  const primaryAction = () => {
    if (loading) return;
    navigate(isAuthenticated ? "/dashboard" : "/registro");
  };

  return (
    <Box component="main" sx={{ bgcolor: "#fff", overflowX: "hidden" }}>
      <Box
        component="section"
        sx={{
          pt: { xs: 15, md: 17 },
          pb: { xs: 7, md: 10 },
          px: { xs: 2, md: 4 },
          background:
            "radial-gradient(circle at 86% 15%, rgba(243,90,58,.2), transparent 24%), linear-gradient(180deg, #FFF9F6 0%, #FFFFFF 100%)",
        }}
      >
        <Container maxWidth="lg" disableGutters>
          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={{ xs: 4, lg: 7 }}
            alignItems={{ xs: "flex-start", lg: "center" }}
          >
            <Box sx={{ flex: 1, maxWidth: 790 }}>
              <Chip
                icon={<DescriptionRoundedIcon />}
                label="Guía operativa pública de WebProfit"
                sx={{ bgcolor: "#FFF0E9", color: "#B83C23", fontWeight: 900, mb: 2.2 }}
              />
              <Typography
                variant="h1"
                sx={{
                  color: BRAND.dark,
                  fontWeight: 950,
                  letterSpacing: "-.055em",
                  lineHeight: 0.98,
                  fontSize: { xs: "2.7rem", sm: "3.7rem", md: "5rem" },
                }}
              >
                WebProfit,
                <Box component="span" sx={{ color: BRAND.orange }}>
                  {" "}de cero a funcionando.
                </Box>
              </Typography>
              <Typography
                sx={{
                  mt: 2.5,
                  maxWidth: 760,
                  color: "rgba(25,36,47,.68)",
                  fontSize: { xs: "1rem", md: "1.16rem" },
                  lineHeight: 1.75,
                }}
              >
                Esta documentación explica el proceso técnico desde la mirada del usuario: qué
                configurar, en qué orden, qué estados atraviesa cada operación, cómo validarla y
                qué revisar cuando algo no funciona. No expone código ni infraestructura interna.
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.4} sx={{ mt: 3.5 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={primaryAction}
                  disabled={loading}
                  startIcon={isAuthenticated ? <DashboardRoundedIcon /> : <RocketLaunchRoundedIcon />}
                  endIcon={!isAuthenticated ? <ArrowForwardRoundedIcon /> : undefined}
                  sx={{
                    minHeight: 54,
                    px: 3,
                    borderRadius: 999,
                    bgcolor: BRAND.orange,
                    textTransform: "none",
                    fontWeight: 900,
                    boxShadow: "0 15px 32px rgba(243,90,58,.24)",
                    "&:hover": { bgcolor: BRAND.orangeDark },
                  }}
                >
                  {isAuthenticated ? "Continuar en mi panel" : "Crear mi cuenta"}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<WhatsAppIcon />}
                  onClick={() => {
                    window.location.href = WHATSAPP_URL;
                  }}
                  sx={{
                    minHeight: 54,
                    px: 2.7,
                    borderRadius: 999,
                    color: BRAND.dark,
                    borderColor: "rgba(25,36,47,.18)",
                    textTransform: "none",
                    fontWeight: 850,
                  }}
                >
                  Hablar con soporte
                </Button>
              </Stack>
            </Box>

            <Paper
              elevation={0}
              sx={{
                width: { xs: "100%", lg: 365 },
                p: 2.4,
                borderRadius: 5,
                border: "1px solid rgba(25,36,47,.1)",
                boxShadow: "0 28px 65px rgba(25,36,47,.1)",
              }}
            >
              <Typography sx={{ color: BRAND.dark, fontWeight: 950, fontSize: "1.05rem", mb: 1.3 }}>
                Buscar por proceso
              </Typography>
              <Stack spacing={0.35}>
                {sections.map(({ id, label, icon: Icon }) => (
                  <Button
                    key={id}
                    onClick={() => scrollToSection(id)}
                    startIcon={<Icon sx={{ fontSize: 19 }} />}
                    sx={{
                      justifyContent: "flex-start",
                      color: "rgba(25,36,47,.72)",
                      textTransform: "none",
                      fontWeight: 800,
                      borderRadius: 2.5,
                      px: 1.4,
                      py: 0.75,
                      "&:hover": { bgcolor: "rgba(243,90,58,.07)", color: BRAND.orangeDark },
                    }}
                  >
                    {label}
                  </Button>
                ))}
              </Stack>
            </Paper>
          </Stack>
        </Container>
      </Box>

      <DocumentationSection id="inicio">
        <SectionHeading
          eyebrow="Puesta en marcha"
          title="El recorrido técnico completo para comenzar"
          description="La implementación se hace por capas. Primero se asegura el acceso y la publicación; después se carga la oferta; finalmente se habilitan ventas, agenda, gestión y equipo. Cada etapa debe cerrarse con una comprobación real."
        />

        <Grid container spacing={2}>
          {launchSteps.map((step) => (
            <Grid item xs={12} md={6} lg={4} key={step.number}>
              <Card
                variant="outlined"
                sx={{
                  height: "100%",
                  borderRadius: 4,
                  borderColor: "rgba(25,36,47,.1)",
                  boxShadow: "0 16px 40px rgba(25,36,47,.05)",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography sx={{ color: "rgba(243,90,58,.28)", fontWeight: 950, fontSize: "2.7rem", lineHeight: 1 }}>
                    {step.number}
                  </Typography>
                  <Typography sx={{ mt: 1.2, color: BRAND.dark, fontWeight: 950, fontSize: "1.12rem" }}>
                    {step.title}
                  </Typography>
                  <Typography sx={{ mt: 1, color: "rgba(25,36,47,.64)", lineHeight: 1.65 }}>
                    {step.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 3 }}>
          <TechnicalGuide
            defaultExpanded
            title="1. Registrar y verificar la cuenta"
            summary="El correo verificado identifica al titular y permite recuperar el acceso con seguridad."
            prerequisites={["Usar un correo al que el responsable tenga acceso permanente.", "Definir una contraseña de al menos seis caracteres y no compartirla con el equipo."]}
            steps={[
              { title: "Completar el registro", action: "Ingresá nombre, correo y contraseña. Si llegaste mediante un vendedor o afiliado, utilizá el enlace que te compartió para conservar correctamente el origen de la cuenta.", result: "La cuenta queda creada en estado pendiente de verificación." },
              { title: "Abrir el mensaje de verificación", action: "Buscá el correo de WebProfit. Abrí el enlace recibido o copiá el código de verificación en la pantalla correspondiente.", result: "El correo queda validado y la cuenta puede continuar con el acceso normal." },
              { title: "Reenviar cuando sea necesario", action: "Si el código venció o no llegó, verificá primero la carpeta de correo no deseado y luego solicitá un nuevo envío.", result: "Se genera una nueva validación; la anterior deja de ser la referencia activa." },
              { title: "Iniciar sesión", action: "Ingresá con el mismo correo verificado y la contraseña definida. No crees otra cuenta para resolver un error de ingreso.", result: "El panel muestra los proyectos asociados a ese usuario." },
            ]}
            verification={["El panel reconoce el nombre del usuario y mantiene la sesión activa.", "La cuenta puede crear un proyecto o abrir uno al que fue invitada.", "El correo que aparece en el panel coincide con el correo verificado."]}
            warnings={["Nunca compartas la contraseña del propietario. Para colaboradores se deben crear accesos separados."]}
            screenshot="registro, confirmación de correo y primera vista del panel"
          />

          <TechnicalGuide
            title="2. Crear el primer proyecto"
            summary="Cada proyecto representa un negocio independiente, con su propia página, información, módulos, equipo y operación."
            prerequisites={["Tener la cuenta verificada.", "Definir el nombre comercial y una dirección pública corta y fácil de compartir.", "Tener logo principal, logo compacto y favicon si ya existen; son opcionales para comenzar."]}
            steps={[
              { title: "Ingresar a Proyectos", action: "Desde el panel, seleccioná la opción para crear o activar un proyecto nuevo.", result: "Se abre el asistente de alta." },
              { title: "Definir nombre y dirección", action: "Cargá el nombre real del negocio y una dirección sin espacios ni caracteres confusos. Evitá nombres temporales porque esa dirección será compartida con clientes.", result: "El sistema valida que la dirección esté disponible." },
              { title: "Cargar identidad inicial", action: "Podés incorporar los tres recursos de marca. Si todavía no están listos, continuá y completalos luego desde Diseñá tu Web.", result: "El proyecto recibe una apariencia inicial coherente." },
              { title: "Confirmar la creación", action: "Revisá nombre y dirección antes de crear. Durante este proceso se prepara el espacio privado y la publicación inicial.", result: "Aparece la tarjeta del proyecto y comienza el período de prueba cuando corresponde." },
              { title: "Esperar el estado publicado", action: "No repitas la creación mientras el sistema está preparando el proyecto. Actualizá el estado desde la misma tarjeta cuando sea necesario.", result: "La dirección pública queda disponible o el panel muestra un error concreto para corregir." },
              { title: "Abrir el editor", action: "Entrá en Diseñá tu Web y completá el contenido antes de promocionar la dirección.", result: "El proyecto pasa de una base genérica a una presentación real del negocio." },
            ]}
            verification={["La tarjeta del proyecto muestra nombre, estado y dirección pública.", "El enlace público abre sin pedir acceso al panel.", "El editor corresponde al mismo negocio y no a otro proyecto."]}
            warnings={["No crees proyectos duplicados cuando una publicación demora. Primero revisá el estado y el mensaje de error del proyecto existente."]}
            screenshot="asistente de creación y tarjeta del proyecto ya publicado"
          />
        </Box>

        <Alert severity="info" sx={{ mt: 3, borderRadius: 3 }}>
          Durante la prueba pueden estar disponibles más herramientas que en el plan definitivo.
          Antes de finalizarla, revisá la mensualidad y los módulos que el negocio realmente utilizará.
        </Alert>
      </DocumentationSection>

      <DocumentationSection id="pagina" shaded>
        <SectionHeading
          eyebrow="Diseñá tu Web"
          title="Configurar la página sección por sección"
          description="El editor trabaja sobre una configuración general y sobre bloques independientes. La forma segura de editar es cambiar una sección, guardar, revisar la vista previa y recién después continuar con la siguiente."
        />

        <Grid container spacing={2.2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <FeatureCard icon={LanguageRoundedIcon} title="Identidad global" description="Datos del negocio, logotipos, tipografías, colores, fondos, radios y modo visual." />
          </Grid>
          <Grid item xs={12} md={4}>
            <FeatureCard icon={DesignServicesRoundedIcon} title="Secciones editables" description="Navbar, portada, destacados, marcas, categorías, productos, nosotros, diferenciales, contacto y footer." />
          </Grid>
          <Grid item xs={12} md={4}>
            <FeatureCard icon={CheckCircleRoundedIcon} title="Publicación controlada" description="Visibilidad por sección, vista previa, revisión móvil, mensajes alternativos y datos para buscadores." />
          </Grid>
        </Grid>

        <TechnicalGuide
          defaultExpanded
          title="1. Completar la identidad y el tema general"
          summary="Estos valores se reutilizan en toda la página; conviene definirlos antes de ajustar cada sección."
          prerequisites={["Logo en buena calidad y con fondo adecuado.", "Paleta de colores con contraste suficiente.", "Nombre, descripción corta, WhatsApp, correo, dirección, horarios y redes sociales."]}
          steps={[
            { title: "Cargar los datos del negocio", action: "Completá nombre comercial, descripción, WhatsApp, correo, teléfono, dirección, ciudad, provincia, país, horarios y enlaces sociales. Escribí los teléfonos con código de área.", result: "Contacto, footer y botones pueden reutilizar datos coherentes." },
            { title: "Subir los recursos de marca", action: "Cargá logo principal, versión compacta y favicon. Usá archivos legibles y evitá imágenes con demasiado espacio vacío alrededor.", result: "La marca se ve correctamente en escritorio, móvil y pestaña del navegador." },
            { title: "Definir colores funcionales", action: "Elegí color principal, secundario, superficies, fondo, texto, texto secundario y divisores. No elijas colores sólo por estética: verificá que títulos y botones se lean.", result: "Todas las secciones comparten una identidad consistente." },
            { title: "Elegir tipografías", action: "Definí una tipografía para títulos y otra para textos, o utilizá la misma familia. Evitá combinar demasiadas fuentes y pesos.", result: "La página conserva jerarquía visual y carga de forma estable." },
            { title: "Ajustar forma y modo", action: "Configurá redondeo de tarjetas y botones, y elegí modo claro u oscuro según el contenido y las imágenes del negocio.", result: "El estilo general queda listo para editar las secciones." },
            { title: "Guardar y recargar", action: "Guardá los ajustes y recargá la vista previa para confirmar que los recursos nuevos quedaron asociados al proyecto correcto.", result: "Los cambios persisten al salir y volver a entrar." },
          ]}
          verification={["El logo no está deformado ni recortado.", "Los textos se leen sobre todos los fondos.", "WhatsApp y redes abren las cuentas correctas.", "Los cambios siguen visibles después de recargar el editor."]}
          screenshot="panel de identidad global y selector de colores"
        />

        <TechnicalGuide
          title="2. Configurar navegación y portada"
          summary="La navegación orienta al visitante; la portada explica qué ofrece el negocio y qué debe hacer después."
          prerequisites={["Tener definido el objetivo principal: consultar, comprar, reservar o conocer el negocio.", "Saber si habrá catálogo, carrito y acceso de clientes."]}
          steps={[
            { title: "Ordenar la barra de navegación", action: "Activá únicamente los enlaces que tengan una sección o una página disponible. Personalizá las etiquetas para que el visitante entienda adónde va.", result: "El menú no contiene accesos vacíos ni repetidos." },
            { title: "Configurar logo y comportamiento móvil", action: "Verificá logo normal, logo compacto, apertura del menú y botones de carrito o cuenta cuando esos módulos estén habilitados.", result: "La navegación funciona en pantallas pequeñas sin ocupar toda la página." },
            { title: "Escribir la portada", action: "Completá frase superior, título, subtítulo y botones. El título debe explicar la propuesta en una oración; el subtítulo agrega contexto, no repite lo mismo.", result: "Un visitante nuevo entiende el negocio en pocos segundos." },
            { title: "Elegir las acciones", action: "El botón principal debe llevar a WhatsApp, contacto, catálogo o reserva. El secundario puede ampliar información. No apuntes a una función que el plan no tiene activa.", result: "Los botones llevan a destinos reales y útiles." },
            { title: "Cargar imagen o carrusel", action: "Usá imágenes reales, definí su posición y agregá texto alternativo. Si utilizás varias diapositivas, mantené un estilo y una proporción similares.", result: "La portada no salta de tamaño ni oculta el texto." },
            { title: "Probar desde la dirección pública", action: "Abrí la página fuera del editor y probá menú, botones, carrito, cuenta y enlaces externos.", result: "La experiencia pública coincide con la vista previa." },
          ]}
          verification={["El menú abre y cierra en móvil.", "Los botones no llevan a una sección desactivada.", "La imagen principal carga sin deformarse.", "El título se entiende sin conocer previamente al negocio."]}
          screenshot="barra de navegación y portada en vista móvil y escritorio"
        />

        <TechnicalGuide
          title="3. Completar los bloques comerciales"
          summary="Estos bloques ayudan a explicar la oferta antes de que el visitante llegue al contacto o al catálogo completo."
          steps={[
            { title: "Destacado principal", action: "Usá el bloque destacado para una promoción, marca, servicio estratégico o campaña temporal. Definí colores, imagen de fondo y una acción concreta.", result: "La promoción se diferencia del resto sin romper la identidad." },
            { title: "Marcas", action: "Cargá nombre, logo y criterio de búsqueda de cada marca. Incluí sólo marcas con publicaciones activas para evitar resultados vacíos.", result: "Cada marca conduce a contenido relacionado." },
            { title: "Productos destacados", action: "Definí título, texto de apoyo y llamada a la acción. La selección se alimenta del catálogo, por lo que primero deben existir publicaciones activas.", result: "La portada muestra una oferta real y actualizada." },
            { title: "Categorías", action: "Usá nombres simples y consistentes. No crees categorías casi iguales por diferencias de mayúsculas, singular o plural.", result: "La navegación del catálogo queda limpia y predecible." },
            { title: "Sobre el negocio", action: "Explicá historia, experiencia, zona de trabajo y especialidad. Evitá textos genéricos que podrían pertenecer a cualquier empresa.", result: "El visitante entiende quién está detrás del servicio." },
            { title: "Diferenciales", action: "Elegí beneficios comprobables: cobertura, rapidez, asesoramiento, garantía, experiencia o modalidad. Cada punto debe incluir título y explicación breve.", result: "La sección justifica por qué elegir al negocio." },
          ]}
          verification={["No hay bloques vacíos visibles.", "Marcas y categorías devuelven resultados.", "Los diferenciales son específicos y verificables.", "Los textos no tienen datos de ejemplo."]}
          screenshot="bloques de marcas, categorías, destacados y diferenciales"
        />

        <TechnicalGuide
          title="4. Contacto, footer, visibilidad y publicación"
          summary="La última etapa conecta al visitante con el negocio y define qué se muestra cuando una función no está disponible."
          steps={[
            { title: "Configurar contacto", action: "Completá título, descripción, mensaje inicial de WhatsApp, mapa cuando corresponda y botones de contacto.", result: "El usuario puede comunicarse sin copiar datos manualmente." },
            { title: "Configurar el botón flotante", action: "Activá el acceso flotante de WhatsApp sólo si el número y el mensaje fueron probados.", result: "El contacto rápido funciona desde cualquier sección." },
            { title: "Cerrar el footer", action: "Definí estilo, logo, texto de cierre, navegación, información de contacto y copyright.", result: "La página termina con información útil y consistente." },
            { title: "Revisar visibilidad de secciones", action: "Activá o desactivá navbar, portada, destacado, marcas, categorías, productos, nosotros, diferenciales, contacto, footer y WhatsApp. Ocultar una sección no reemplaza completar otra.", result: "Sólo se muestran bloques terminados." },
            { title: "Preparar mensajes alternativos", action: "Revisá los mensajes que aparecen cuando el sitio está en borrador, mantenimiento o cuando catálogo, pedidos o pagos no están disponibles.", result: "El visitante recibe una explicación útil en lugar de un error técnico." },
            { title: "Completar datos para buscadores", action: "Definí título, descripción e imagen social. Escribí como buscaría un cliente real, sin repetir palabras de forma artificial.", result: "La página queda preparada para compartirse y ser identificada correctamente." },
            { title: "Publicar y hacer control cruzado", action: "Guardá, publicá y revisá desde un teléfono con datos móviles y desde una computadora. Probá todos los enlaces en una ventana privada.", result: "La página queda lista para difusión." },
          ]}
          verification={["No se muestran secciones desactivadas.", "El sitio publicado refleja el último guardado.", "WhatsApp, mapa, redes y correo abren correctamente.", "La vista móvil no tiene textos cortados ni desplazamiento horizontal."]}
          warnings={["No promociones la página mientras esté en borrador o con datos genéricos."]}
          screenshot="control de secciones, vista previa final y dirección pública"
        />
      </DocumentationSection>

      <DocumentationSection id="catalogo">
        <SectionHeading
          eyebrow="Productos y servicios"
          title="Cargar una oferta técnicamente completa"
          description="Productos y servicios comparten catálogo, pero no comparten toda su lógica. Elegir bien el tipo determina si se controla stock, si se permite comprar, si se muestran horarios y si el cliente debe reservar."
        />

        <TechnicalGuide
          defaultExpanded
          title="1. Crear una publicación base"
          summary="Nombre, categoría y precio son el núcleo; el resto mejora la búsqueda, la presentación y la operación."
          prerequisites={["Tener activo el módulo Productos y servicios.", "Definir categorías antes de cargar grandes cantidades.", "Preparar imágenes optimizadas y una descripción comercial real."]}
          steps={[
            { title: "Elegir producto o servicio", action: "Seleccioná Producto para artículos físicos o unidades vendibles. Seleccioná Servicio para prestaciones, turnos, asesorías, trabajos o actividades.", result: "El formulario habilita los campos apropiados." },
            { title: "Completar identificación", action: "Ingresá nombre, categoría, descripción, código interno y unidad cuando corresponda. Usá nombres únicos y fáciles de buscar.", result: "La publicación puede encontrarse y distinguirse de otras." },
            { title: "Definir la presentación del precio", action: "Elegí precio fijo cuando se puede comprar al valor indicado, desde cuando es un valor inicial o consultar cuando requiere presupuesto. Aunque se muestre como consulta, conservá un valor administrativo coherente cuando el panel lo solicite.", result: "La web comunica correctamente si existe compra directa o consulta." },
            { title: "Agregar imágenes", action: "Subí varias imágenes cuando aporten información distinta. Revisá la primera porque normalmente funciona como portada del artículo.", result: "La publicación tiene imagen principal y galería." },
            { title: "Agregar etiquetas y atributos", action: "Usá etiquetas para filtros o conceptos repetidos. En atributos podés registrar marca, material, cobertura, nivel, tamaño u otros datos propios del rubro.", result: "La ficha se adapta sin crear un sistema diferente para cada negocio." },
            { title: "Definir visibilidad", action: "Guardá como activa sólo una publicación lista para mostrarse. Desactivala si está incompleta, discontinuada o fuera de temporada.", result: "El catálogo público muestra únicamente ofertas utilizables." },
          ]}
          verification={["La publicación aparece en su categoría correcta.", "La primera imagen carga y las demás se pueden recorrer.", "El tipo de precio se muestra como fue definido.", "Al desactivarla deja de aparecer públicamente sin borrar su información."]}
          screenshot="formulario completo de producto o servicio y ficha pública"
        />

        <TechnicalGuide
          title="2. Configurar un producto físico y su stock"
          summary="El stock debe representar unidades reales; los pedidos reservan, confirman o liberan esas unidades según su estado."
          prerequisites={["Definir una unidad de venta consistente.", "Contar físicamente las existencias iniciales.", "Evitar que dos publicaciones representen el mismo stock sin una regla clara."]}
          steps={[
            { title: "Ingresar la existencia inicial", action: "Cargá la cantidad disponible real. No utilices el stock para representar capacidad de servicio o disponibilidad horaria.", result: "El panel conoce el punto de partida." },
            { title: "Confirmar precio y compra directa", action: "Para ventas online, usá precio fijo y verificá que el producto esté activo. Los precios desde o a consultar pueden requerir coordinación en lugar de checkout automático.", result: "El flujo de venta coincide con el tipo de precio." },
            { title: "Registrar entradas y salidas", action: "Cuando el módulo de gestión esté activo, utilizá movimientos de stock para compras, ajustes, pérdidas o devoluciones. Evitá corregir cantidades sin dejar un motivo.", result: "Existe trazabilidad entre stock anterior y posterior." },
            { title: "Entender la reserva", action: "Al iniciar un pedido, las unidades pueden quedar reservadas temporalmente para evitar sobreventa. La aprobación del pago confirma la salida; el vencimiento o rechazo libera la reserva.", result: "El mismo stock no se promete a varios compradores." },
            { title: "Recontar periódicamente", action: "Compará existencia del sistema con existencia física y registrá ajustes identificados cuando haya diferencia.", result: "El catálogo y la operación trabajan sobre cantidades confiables." },
          ]}
          statusFlow={["Disponible", "Reservado", "Confirmado", "Entregado"]}
          verification={["El stock baja únicamente con una operación válida.", "Un pedido cancelado o vencido devuelve las unidades reservadas.", "Los ajustes muestran cantidad anterior, cambio y cantidad final."]}
          warnings={["No elimines un producto con pedidos asociados. Desactivalo para conservar el historial."]}
          screenshot="stock actual, historial de movimientos y pedido que reservó unidades"
        />

        <TechnicalGuide
          title="3. Configurar un servicio reservable"
          summary="Un servicio necesita reglas de tiempo y capacidad; el precio por sí solo no alcanza para ofrecer turnos."
          prerequisites={["Tener activos Productos y servicios y Agenda.", "Definir duración real, días de atención y modalidad.", "Decidir si se confirma automáticamente o mediante revisión."]}
          steps={[
            { title: "Definir duración y modalidad", action: "Ingresá la duración en minutos y especificá si es presencial, virtual, a domicilio o una modalidad propia. Agregá ubicación o enlace cuando corresponda.", result: "Cada turno calcula inicio y fin correctamente." },
            { title: "Activar la reserva", action: "Indicá que el servicio requiere reserva. Si sólo querés recibir consultas, mantenelo como servicio sin agenda automática.", result: "La página ofrece horarios únicamente para servicios preparados." },
            { title: "Elegir confirmación", action: "Usá confirmación automática cuando la disponibilidad sea definitiva; usá manual cuando el negocio necesite revisar cada solicitud.", result: "El estado inicial del turno coincide con el proceso del negocio." },
            { title: "Definir pago de reserva", action: "Elegí sin pago, pago total o seña. Para una seña, definí valor fijo o porcentaje y asegurate de que la integración de cobro esté activa.", result: "El cliente conoce cuánto debe abonar para reservar." },
            { title: "Configurar capacidad", action: "Usá capacidad 1 para atención individual. Aumentala para clases, cupos o grupos que comparten el mismo horario.", result: "La agenda deja de ofrecer el horario cuando alcanza el cupo." },
            { title: "Agregar tiempos de preparación", action: "Configurá minutos antes y después del servicio para traslado, limpieza, preparación o cierre. Estos tiempos bloquean agenda aunque no se muestren como duración comercial.", result: "No se generan turnos consecutivos imposibles de atender." },
            { title: "Definir cancelación e instrucciones", action: "Agregá ventana de cancelación e indicaciones previas: documentación, puntualidad, dirección, materiales o condiciones.", result: "El cliente recibe reglas claras antes de confirmar." },
          ]}
          verification={["La ficha pública muestra modalidad, duración y precio correctos.", "Los horarios respetan duración y buffers.", "La capacidad se descuenta con reservas vigentes.", "El importe de seña o pago total coincide con la configuración."]}
          screenshot="configuración avanzada de un servicio y selector público de horario"
        />
      </DocumentationSection>

      <DocumentationSection id="ventas" shaded>
        <SectionHeading
          eyebrow="Pedidos y cobros"
          title="Conectar catálogo, carrito, pago y entrega"
          description="Un pedido no es sólo un formulario: reúne datos del cliente, artículos, total, modalidad de entrega, estado comercial, estado del pago y estado del stock. Cada parte debe avanzar de forma coordinada."
        />

        <TechnicalGuide
          defaultExpanded
          title="1. Preparar la tienda antes de vender"
          summary="La tienda debe conocer qué puede vender, cómo se entrega y cómo se cobra."
          prerequisites={["Módulos Productos y Pedidos activos.", "Publicaciones activas, con precio y stock correctos.", "Datos de contacto del negocio completos.", "Módulo Pagos activo si se utilizará Mercado Pago."]}
          steps={[
            { title: "Revisar las publicaciones comprables", action: "Confirmá que los artículos del carrito tengan precio fijo, estén activos, posean imágenes y, si controlan stock, tengan unidades disponibles.", result: "El cliente puede agregarlos sin encontrar inconsistencias." },
            { title: "Elegir la forma de cobro", action: "Definí si el pedido se coordina con el comercio o si continúa a Mercado Pago. La coordinación puede usarse para transferencia, efectivo o presupuesto posterior.", result: "Cada pedido nace con el modo de pago correcto." },
            { title: "Conectar Mercado Pago", action: "Desde la configuración de cobros, vinculá la cuenta comercial indicada y verificá que la conexión figure activa. La cuenta debe pertenecer al negocio que recibirá el dinero.", result: "El checkout puede crear una operación de pago válida." },
            { title: "Definir retiro o entrega", action: "Habilitá retiro, entrega o ambas opciones según el negocio. Para entrega, asegurate de solicitar calle, número, ciudad, provincia y código postal cuando sea necesario.", result: "El pedido contiene la información logística suficiente." },
            { title: "Probar con un pedido controlado", action: "Realizá una compra desde la web pública con un artículo de bajo riesgo y datos identificables como prueba.", result: "El pedido aparece en el panel y conserva cliente, artículos, totales y modalidad." },
          ]}
          verification={["El carrito calcula cantidades y subtotal correctamente.", "El pedido aparece una sola vez en el panel.", "La modalidad de entrega conserva la dirección cuando corresponde.", "La cuenta de pago que recibe el dinero es la del comercio correcto."]}
          warnings={["La mensualidad de WebProfit y los pagos que recibe la tienda son circuitos distintos. No conectes una cuenta de cobro equivocada."]}
          screenshot="configuración de e-commerce, carrito y primer pedido de prueba"
        />

        <TechnicalGuide
          title="2. Gestionar el ciclo completo del pedido"
          summary="El estado comercial y el estado del pago se observan juntos, pero no significan lo mismo."
          steps={[
            { title: "Pedido recibido", action: "Revisá nombre, correo, teléfono, artículos, cantidades, total, notas y entrega. Si se coordina el pago, contactá al cliente y documentá el avance.", result: "El pedido queda validado para continuar." },
            { title: "Pago pendiente o en proceso", action: "No prepares una entrega irreversible hasta conocer el resultado. Un pago puede permanecer pendiente, autorizado o en proceso antes de aprobarse.", result: "La operación conserva stock reservado mientras corresponde." },
            { title: "Pago aprobado", action: "Al aprobarse, verificá importe, identificador de pago y fecha. El pedido debe pasar a pagado y el stock reservado debe quedar comprometido.", result: "El negocio puede comenzar la preparación con respaldo del cobro." },
            { title: "Preparación y entrega", action: "Actualizá el pedido a procesando, listo, enviado y entregado según el caso. Para retiro, podés omitir enviado y pasar de listo a entregado.", result: "El panel refleja la situación real y no sólo el pago." },
            { title: "Fallo, cancelación o vencimiento", action: "Cuando el pago se rechaza o el pedido se cancela, comprobá que el stock reservado se libere. Registrá una nota si hubo coordinación externa.", result: "Las unidades vuelven a estar disponibles y el pedido queda cerrado." },
            { title: "Reembolso o contracargo", action: "Marcá y revisá la operación cuando el proveedor informa devolución o contracargo. No lo trates como una simple cancelación previa al pago.", result: "El historial conserva que existió un cobro y luego fue revertido." },
          ]}
          statusFlow={["Esperando pago", "Pagado", "Procesando", "Listo", "Enviado", "Entregado"]}
          verification={["El importe del pedido coincide con el importe aprobado.", "El estado del pago no se confunde con el estado de preparación.", "El stock queda confirmado al cobrar y liberado al fallar o cancelar.", "La operación conserva intentos de pago sin duplicar el pedido."]}
          screenshot="detalle de pedido con estado comercial, pago y stock"
        />

        <TechnicalGuide
          title="3. Diagnosticar un cobro que no se actualiza"
          summary="La revisión debe comenzar por el pedido concreto, no creando otro checkout sin entender el anterior."
          steps={[
            { title: "Identificar el pedido", action: "Anotá número de pedido, correo del comprador, importe y horario aproximado.", result: "Soporte puede localizar una única operación." },
            { title: "Comparar ambos estados", action: "Revisá estado del pedido y estado del pago. Puede existir un pedido esperando pago con un intento rechazado o pendiente.", result: "Se identifica si el problema es comercial o de cobro." },
            { title: "Verificar el pago en Mercado Pago", action: "Buscá el movimiento en la cuenta conectada y compará importe, fecha y estado. No uses capturas sin identificador como única prueba.", result: "Se confirma el estado informado por el proveedor." },
            { title: "Actualizar sin duplicar", action: "Utilizá las acciones de revisión disponibles en el panel. Evitá crear manualmente otro pedido con el mismo pago.", result: "El pedido original conserva la trazabilidad." },
            { title: "Escalar con datos completos", action: "Enviá a soporte proyecto, pedido, pago, importe, correo y captura del estado. Nunca compartas contraseñas ni credenciales privadas.", result: "La revisión se realiza sobre información suficiente." },
          ]}
          verification={["Existe un único pedido para la compra.", "El identificador del pago coincide con el proveedor.", "El stock no se descuenta dos veces."]}
          screenshot="número de pedido e identificador del pago, ocultando datos sensibles"
        />
      </DocumentationSection>

      <DocumentationSection id="agenda">
        <SectionHeading
          eyebrow="Turnos y agenda"
          title="Construir disponibilidad sin superposiciones"
          description="La disponibilidad final surge de varias reglas al mismo tiempo: horarios semanales, vigencia, zona horaria, duración, buffers, capacidad, bloqueos, reservas existentes y estado del pago."
        />

        <TechnicalGuide
          defaultExpanded
          title="1. Crear reglas de disponibilidad"
          summary="Las reglas indican qué días y franjas puede ofrecerse cada servicio."
          prerequisites={["Servicio activo y marcado como reservable.", "Duración y capacidad definidas.", "Zona horaria correcta del proyecto."]}
          steps={[
            { title: "Seleccionar el servicio", action: "Configurá disponibilidad por servicio. No asumas que todos comparten los mismos horarios o duración.", result: "Las reglas quedan asociadas a la prestación correcta." },
            { title: "Crear franjas semanales", action: "Elegí día de la semana, hora de inicio y hora de fin. Podés crear más de una franja para separar mañana y tarde.", result: "La agenda conoce el horario base recurrente." },
            { title: "Definir vigencia cuando corresponda", action: "Usá fecha desde y hasta para horarios de temporada, promociones o cambios temporales. Sin vigencia, la regla se considera estable.", result: "La agenda no ofrece reglas fuera de su período." },
            { title: "Verificar zona horaria", action: "Confirmá la zona del proyecto antes de cargar turnos. Es especialmente importante para servicios virtuales o clientes de otras regiones.", result: "La hora guardada y la hora mostrada coinciden." },
            { title: "Guardar y simular", action: "Abrí el selector público para una fecha futura y comprobá que aparezcan intervalos posibles según duración y buffers.", result: "Los horarios ofrecidos son realmente atendibles." },
          ]}
          verification={["No se ofrecen horarios fuera de las franjas.", "La última reserva entra completa antes del cierre.", "Los buffers se respetan aunque no aparezcan como parte de la duración.", "La zona horaria se muestra correctamente."]}
          screenshot="regla semanal y selector público de disponibilidad"
        />

        <TechnicalGuide
          title="2. Bloquear fechas y administrar excepciones"
          summary="Los bloqueos reemplazan temporalmente la disponibilidad por vacaciones, feriados, tareas internas o eventos."
          steps={[
            { title: "Elegir alcance", action: "Creá un bloqueo general para afectar toda la agenda o elegí un servicio específico si los demás siguen funcionando.", result: "Sólo se bloquea el alcance necesario." },
            { title: "Definir inicio y fin", action: "Ingresá fecha y hora de comienzo y final. El final siempre debe ser posterior al inicio.", result: "La agenda puede comparar el bloqueo con los turnos posibles." },
            { title: "Registrar el motivo", action: "Escribí una razón breve y útil para el equipo: feriado, mantenimiento, capacitación, traslado o reserva externa.", result: "El bloqueo puede auditarse sin depender de la memoria." },
            { title: "Revisar reservas existentes", action: "Antes de bloquear, verificá si ya existen turnos confirmados. El bloqueo de disponibilidad no reemplaza avisar o reprogramar a esos clientes.", result: "No quedan reservas activas sin atención prevista." },
            { title: "Desactivar en lugar de borrar cuando convenga", action: "Para conservar contexto, desactivá bloqueos históricos o creados por error cuando el panel lo permita.", result: "La agenda vuelve a ofrecer horarios sin perder referencia." },
          ]}
          verification={["El período bloqueado desaparece de la reserva pública.", "Otros servicios continúan disponibles si el bloqueo era específico.", "Las reservas anteriores fueron revisadas individualmente."]}
          screenshot="bloqueo de calendario general y bloqueo por servicio"
        />

        <TechnicalGuide
          title="3. Gestionar una reserva desde solicitud hasta cierre"
          summary="El turno conserva una fotografía del servicio, los datos del cliente, horario, estado y pago asociado."
          steps={[
            { title: "Revisar la solicitud", action: "Confirmá cliente, teléfono, correo, servicio, modalidad, fecha, duración, notas y origen de la reserva.", result: "La solicitud contiene datos suficientes para atenderse." },
            { title: "Confirmar o mantener pendiente", action: "En confirmación manual, revisá disponibilidad y condiciones antes de confirmar. En automática, controlá igualmente nuevas reservas durante la operación diaria.", result: "El estado representa el compromiso real del negocio." },
            { title: "Revisar el requisito de pago", action: "Si exige seña o pago total, compará importe esperado, importe pagado y estado. No marques completado sólo porque el turno fue creado.", result: "La reserva y el cobro avanzan coordinados." },
            { title: "Atender y cerrar", action: "Después del servicio, marcá completado. Si el cliente no asistió, usá ausente. Si se cancela, registrá el motivo.", result: "La agenda histórica permite medir atención y ausencias." },
            { title: "Reprogramar con control", action: "Antes de cambiar horario, verificá la nueva disponibilidad completa, incluidos buffers y capacidad. Informá al cliente por el canal acordado.", result: "La nueva reserva no se superpone y el cliente conoce el cambio." },
          ]}
          statusFlow={["Pendiente", "Confirmado", "Completado"]}
          verification={["Inicio, fin y período ocupado son coherentes.", "El turno no supera la capacidad del servicio.", "El pago requerido tiene estado e importe correctos.", "Cancelaciones y ausencias no se cuentan como completadas."]}
          screenshot="detalle del turno con servicio, horario, estado y pago"
        />
      </DocumentationSection>

      <DocumentationSection id="gestion" shaded>
        <SectionHeading
          eyebrow="Sistema de gestión"
          title="Registrar la operación sin perder trazabilidad"
          description="El sistema integra movimientos operativos, transacciones, caja, stock y libro de cuentas. La regla principal es no borrar la historia: cuando una operación fue registrada incorrectamente, debe anularse o revertirse y luego cargarse de forma correcta."
        />

        <Grid container spacing={2.2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} lg={3}><FeatureCard icon={PointOfSaleRoundedIcon} title="Caja" description="Cajas, sesiones, apertura, ingresos, egresos, ajustes y cierre." /></Grid>
          <Grid item xs={12} sm={6} lg={3}><FeatureCard icon={Inventory2RoundedIcon} title="Stock" description="Entradas, salidas, ajustes, reversas y saldo por producto." /></Grid>
          <Grid item xs={12} sm={6} lg={3}><FeatureCard icon={LocalAtmRoundedIcon} title="Transacciones" description="Cobros, pagos, devoluciones, transferencias y ajustes por medio." /></Grid>
          <Grid item xs={12} sm={6} lg={3}><FeatureCard icon={DescriptionRoundedIcon} title="Cuentas" description="Registros de débito y crédito para conocer de dónde sale y adónde va cada importe." /></Grid>
        </Grid>

        <TechnicalGuide
          defaultExpanded
          title="1. Preparar cuentas y cajas"
          summary="Antes de registrar movimientos diarios, el negocio debe definir dónde representa efectivo, bancos, ingresos, gastos, clientes, proveedores y stock."
          prerequisites={["Definir responsables de caja y administración.", "Listar medios de pago usados por el negocio.", "Acordar una moneda principal para la operación cotidiana."]}
          steps={[
            { title: "Revisar el plan de cuentas", action: "Confirmá que existan cuentas activas para activos, obligaciones, patrimonio, ingresos y gastos. Las cuentas del sistema no deberían modificarse sin comprender su función.", result: "Cada movimiento puede asignar origen y destino." },
            { title: "Crear las cajas necesarias", action: "Creá una caja por punto de cobro o responsabilidad cuando necesites cierres separados. Evitá crear una caja por empleado si todos trabajan sobre el mismo efectivo.", result: "La operación puede medirse por caja real." },
            { title: "Asignar responsable", action: "Definí quién puede abrir, operar y cerrar cada caja. El equipo debe usar cuentas individuales para identificar al actor.", result: "Cada acción conserva responsable y fecha." },
            { title: "Acordar saldos iniciales", action: "Antes de la primera apertura, contá efectivo y verificá saldos bancarios de referencia. Registrá ajustes iniciales con una explicación clara.", result: "El sistema comienza desde valores verificables." },
          ]}
          verification={["No existe más de una sesión abierta para la misma caja.", "Cada caja tiene moneda y responsable definidos.", "Las cuentas utilizadas están activas y correctamente clasificadas."]}
          screenshot="listado de cuentas y configuración de cajas"
        />

        <TechnicalGuide
          title="2. Operar una sesión de caja"
          summary="La sesión reúne todos los movimientos de una jornada o turno y permite comparar saldo esperado con efectivo declarado."
          steps={[
            { title: "Abrir la caja", action: "Seleccioná la caja, ingresá el monto inicial contado y agregá una nota si existe diferencia respecto del cierre anterior.", result: "Se crea una única sesión abierta con responsable y hora." },
            { title: "Registrar ingresos y egresos", action: "Cada movimiento debe incluir importe, sentido, cuenta relacionada, descripción, referencia y medio. Usá efectivo sólo cuando el dinero entra o sale físicamente de esa caja.", result: "El saldo esperado se actualiza con cada operación." },
            { title: "Separar otros medios", action: "Transferencia, tarjeta o billetera digital deben registrarse contra sus cuentas correspondientes; no deben aumentar el efectivo físico.", result: "El cierre de caja no mezcla dinero en mano con saldos electrónicos." },
            { title: "Realizar ajustes justificados", action: "Si hay una diferencia real durante la jornada, registrá un ajuste con motivo y responsable. No cambies el saldo inicial para ocultarla.", result: "La corrección queda visible en el historial." },
            { title: "Contar y cerrar", action: "Al finalizar, contá el efectivo, ingresá el importe declarado y revisá la diferencia contra el saldo esperado. Agregá observaciones antes de confirmar.", result: "La sesión queda cerrada con saldo esperado, declarado y diferencia." },
          ]}
          statusFlow={["Caja cerrada", "Sesión abierta", "Movimientos", "Arqueo", "Sesión cerrada"]}
          verification={["Los medios electrónicos no alteran el efectivo físico.", "El cierre muestra quién abrió y quién cerró.", "Las diferencias tienen una explicación o un ajuste asociado."]}
          warnings={["No compartas un único usuario para operar caja; se pierde la identificación del responsable."]}
          screenshot="apertura, movimientos del día y pantalla de cierre"
        />

        <TechnicalGuide
          title="3. Registrar movimientos, cobros y pagos"
          summary="Una operación económica puede generar varias consecuencias: transacción, caja, cuentas y eventualmente stock."
          steps={[
            { title: "Elegir el tipo de operación", action: "Seleccioná venta, compra, gasto, cobro, pago, transferencia, devolución, ajuste o reversa según lo ocurrido realmente.", result: "El sistema aplica el tratamiento correcto." },
            { title: "Completar datos comunes", action: "Ingresá fecha, importe, moneda, descripción, referencia, contraparte y responsable. Usá referencias que permitan volver al comprobante o pedido.", result: "La operación puede auditarse." },
            { title: "Definir origen y destino", action: "Indicá de qué cuenta sale el valor y a qué cuenta ingresa. Una transferencia mueve saldo entre cuentas; un gasto afecta una cuenta de gasto y una forma de pago.", result: "El movimiento queda equilibrado y clasificado." },
            { title: "Vincular caja cuando corresponda", action: "Si el método es efectivo, seleccioná caja y sesión abierta. Para otros medios, no fuerces una caja física.", result: "El saldo de caja sólo refleja efectivo real." },
            { title: "Revisar antes de confirmar", action: "Controlá signo, importe, moneda, cuentas y fecha. Una vez confirmado, corregí mediante anulación o reversa, no editando la historia.", result: "El movimiento se publica una sola vez." },
            { title: "Consultar el detalle", action: "Abrí el movimiento y verificá transacción, líneas contables, movimiento de caja y stock relacionados.", result: "Todas las consecuencias de la operación son visibles desde una referencia común." },
          ]}
          verification={["Débitos y créditos totalizan el mismo importe.", "Los movimientos en efectivo pertenecen a una sesión abierta.", "No existen operaciones duplicadas con la misma referencia.", "Las anulaciones generan una reversa identificable."]}
          screenshot="formulario de movimiento y detalle con sus registros relacionados"
        />

        <TechnicalGuide
          title="4. Controlar stock desde el sistema"
          summary="El historial de stock debe explicar cada cambio y conservar el valor anterior y posterior."
          steps={[
            { title: "Entrada", action: "Usala para compras, devoluciones recibidas o incorporación de unidades. Cargá cantidad, producto, costo cuando corresponda y motivo.", result: "El stock aumenta y queda registrada la procedencia." },
            { title: "Salida", action: "Usala para consumos, pérdidas o entregas no originadas automáticamente por un pedido. No dupliques una salida ya generada por una venta.", result: "El stock disminuye una sola vez." },
            { title: "Ajuste", action: "Usalo después de un recuento físico cuando el saldo del sistema no coincide. Registrá la diferencia, no una venta ficticia.", result: "El sistema coincide con la existencia física y conserva la explicación." },
            { title: "Reversa", action: "Si un movimiento fue incorrecto, generá su reversa. La reversa devuelve el efecto manteniendo el original visible.", result: "La trazabilidad no se rompe." },
            { title: "Conciliación", action: "Revisá periódicamente productos con alta rotación, stock negativo o muchas correcciones. Compará pedidos, movimientos y conteo físico.", result: "Se detectan fallas operativas antes de afectar ventas." },
          ]}
          verification={["Cada cambio muestra stock anterior y posterior.", "No se registran salidas manuales duplicando pedidos.", "Las reversas apuntan al movimiento original."]}
          screenshot="historial de stock filtrado por producto"
        />
      </DocumentationSection>

      <DocumentationSection id="equipo">
        <SectionHeading
          eyebrow="Equipo y accesos"
          title="Dar a cada persona el acceso necesario"
          description="Los colaboradores deben entrar con su propia cuenta. El rol del proyecto define qué tareas pueden realizar; el estado global de la cuenta define si puede iniciar sesión."
        />

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            ["Propietario", "Control general del proyecto, accesos y decisiones principales."],
            ["Administrador", "Configuración y operación general sin reemplazar la titularidad."],
            ["Vendedor", "Productos, clientes, pedidos y seguimiento comercial."],
            ["Depósito", "Existencias, preparación y movimientos de mercadería."],
            ["Facturación", "Cobros, pagos y tareas administrativas relacionadas."],
            ["Visualización", "Consulta información sin administrar toda la cuenta."],
          ].map(([title, description]) => (
            <Grid item xs={12} sm={6} md={4} key={title}>
              <Paper elevation={0} sx={{ height: "100%", p: 2.4, borderRadius: 3.5, border: "1px solid rgba(25,36,47,.1)" }}>
                <Typography sx={{ color: BRAND.dark, fontWeight: 950 }}>{title}</Typography>
                <Typography sx={{ mt: 0.8, color: "rgba(25,36,47,.62)", lineHeight: 1.6 }}>{description}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <TechnicalGuide
          defaultExpanded
          title="Agregar, revisar o quitar un miembro"
          summary="El acceso se asigna por proyecto; una misma persona puede tener roles distintos en negocios diferentes."
          prerequisites={["La persona debe tener un correo propio.", "Definir previamente qué tareas realizará.", "El propietario o administrador debe tener permiso para gestionar accesos."]}
          steps={[
            { title: "Abrir Configuración y Accesos", action: "Ingresá al proyecto correcto y verificá su nombre antes de modificar miembros.", result: "La gestión se realiza sobre el negocio esperado." },
            { title: "Ingresar el correo", action: "Usá el correo con el que la persona se registró o se registrará. Evitá correos compartidos entre varios empleados.", result: "El miembro queda asociado a una identidad individual." },
            { title: "Seleccionar el rol mínimo suficiente", action: "Asigná sólo las responsabilidades necesarias. Un vendedor no necesita control total; un usuario de consulta no necesita permisos de edición.", result: "Se reduce el riesgo de cambios accidentales." },
            { title: "Confirmar el alta", action: "Guardá y verificá que el miembro aparezca con nombre, correo, rol y estado de cuenta.", result: "El acceso queda registrado en el proyecto." },
            { title: "Probar con la cuenta del miembro", action: "Pedile que inicie sesión y compruebe que ve el proyecto y únicamente las funciones correspondientes.", result: "El rol funciona en la práctica." },
            { title: "Retirar accesos inmediatamente", action: "Cuando una persona deja el equipo o cambia de función, eliminá su membresía o ajustá el rol. Cambiar la contraseña del propietario no reemplaza esta acción.", result: "El proyecto conserva una lista actual de responsables." },
          ]}
          verification={["Cada miembro aparece una sola vez por proyecto.", "El rol coincide con las tareas reales.", "Una persona retirada ya no puede abrir ese proyecto.", "El propietario mantiene al menos un acceso seguro y verificado."]}
          warnings={["No otorgues rol de propietario o administrador para resolver temporalmente un permiso. Corregí el rol específico que corresponda."]}
          screenshot="tabla de miembros y formulario para asignar un rol"
        />
      </DocumentationSection>

      <DocumentationSection id="mensualidad" shaded>
        <SectionHeading
          eyebrow="Plan y mensualidad"
          title="Entender qué se cobra y qué acceso habilita"
          description="Cada proyecto tiene módulos activos y una mensualidad total. El importe puede incluir servicios recurrentes, condiciones particulares y precios específicos para ese cliente. El estado de billing determina el nivel de acceso al proyecto."
        />

        <TechnicalGuide
          defaultExpanded
          title="1. Revisar módulos y mensualidad total"
          summary="La selección debe representar lo que el cliente realmente utiliza, no todas las posibilidades de la plataforma."
          steps={[
            { title: "Abrir Suscripción", action: "Entrá al proyecto y revisá el resumen actual: estado, módulos, servicios, importe, moneda y próxima fecha cuando corresponda.", result: "Existe una base clara antes de hacer cambios." },
            { title: "Identificar dependencias", action: "Algunas herramientas necesitan otras. Por ejemplo, pedidos requiere catálogo y los cobros online requieren el circuito de pedidos preparado.", result: "No se activa una función aislada que luego no puede operar." },
            { title: "Seleccionar servicios", action: "Agregá o quitá servicios según la necesidad comercial. El panel calcula el total usando precios vigentes y cualquier condición particular autorizada.", result: "La mensualidad refleja la combinación elegida." },
            { title: "Revisar el total antes de pagar", action: "Confirmá importe, moneda, modalidad y correo pagador. El vendedor debe comunicar el total completo, no sólo el precio de un módulo individual.", result: "Cliente y WebProfit comparten la misma referencia económica." },
            { title: "Confirmar el cambio", action: "Completá el checkout o la activación administrativa según la modalidad. Los cambios pendientes no siempre habilitan herramientas hasta que el pago o la aprobación finaliza.", result: "El proyecto actualiza sus módulos y estado cuando la operación se confirma." },
          ]}
          verification={["La suma de servicios coincide con la mensualidad mostrada.", "Los módulos activos coinciden con lo contratado.", "El proyecto no conserva una selección pendiente innecesaria.", "La próxima fecha de pago corresponde al período activo."]}
          screenshot="resumen de suscripción, módulos seleccionados y total"
        />

        <TechnicalGuide
          title="2. Elegir modalidad de pago y comprender estados"
          summary="La suscripción puede cobrarse de forma recurrente, por períodos individuales o administrarse manualmente según la condición del proyecto."
          steps={[
            { title: "Elegir modalidad", action: "Usá recurrente cuando el cliente autoriza renovaciones periódicas. Usá pago por período cuando prefiere confirmar cada vencimiento. La modalidad manual queda reservada para casos administrados por WebProfit.", result: "El circuito de cobro coincide con el acuerdo comercial." },
            { title: "Completar el pago", action: "Ingresá el correo pagador y finalizá en el medio indicado. Volvé al panel para revisar el resultado; no confíes únicamente en la pantalla externa.", result: "El proyecto recibe el estado del pago." },
            { title: "Estado de prueba", action: "Durante el trial, controlá fecha de inicio y finalización. Prepará la continuidad antes del vencimiento para evitar interrupciones.", result: "El cliente conoce qué conservará después de la prueba." },
            { title: "Pago pendiente o vencido", action: "Revisá el último intento, correo pagador y fecha. Durante un período de gracia puede mantenerse acceso limitado por un tiempo definido.", result: "La regularización se hace sobre el proyecto correcto." },
            { title: "Acceso limitado", action: "Según la política, el proyecto puede conservar sólo la presencia pública, entrar en modo de gracia o quedar suspendido. La información no debe confundirse con una eliminación.", result: "El cliente entiende por qué una herramienta dejó de estar disponible." },
            { title: "Cancelación", action: "Verificá si la cancelación es inmediata o al final del período. Antes de cerrar, revisá operaciones pendientes y exportaciones necesarias.", result: "El acceso finaliza de forma ordenada y con fecha conocida." },
          ]}
          statusFlow={["Prueba", "Activo", "Pendiente", "Vencido", "Gracia", "Limitado o suspendido"]}
          verification={["El último pago tiene fecha, importe y estado reconocibles.", "La modalidad elegida coincide con lo acordado.", "El acceso del proyecto coincide con el estado de la suscripción."]}
          screenshot="estado de suscripción, modalidad y próxima fecha"
        />

        <Alert severity="info" sx={{ mt: 3, borderRadius: 3 }}>
          La mensualidad que el cliente paga a WebProfit, los cobros que su negocio recibe de compradores
          y los movimientos internos de caja son tres circuitos independientes.
        </Alert>
      </DocumentationSection>

      <DocumentationSection id="afiliados">
        <SectionHeading
          eyebrow="Programa de referidos"
          title="Registrar la red y calcular comisiones sobre pagos reales"
          description="El usuario conserva una relación de referencia; no guarda un porcentaje fijo. Cada mensualidad aprobada se calcula con la política vigente, la profundidad de la red y el porcentaje total que WebProfit destina al programa."
        />

        <TechnicalGuide
          defaultExpanded
          title="1. Compartir el enlace y registrar un referido"
          summary="El origen debe quedar identificado en el momento del registro. Agregarlo después puede afectar la trazabilidad comercial."
          prerequisites={["Tener un código o enlace personal activo.", "Explicar que la comisión depende de mensualidades reales y aprobadas.", "No prometer un porcentaje permanente si la política puede cambiar en el futuro."]}
          steps={[
            { title: "Copiar el enlace personal", action: "Desde Configuración, abrí el programa de referidos y copiá el enlace completo. No modifiques manualmente el código incluido.", result: "El enlace identifica al afiliado que originó la cuenta." },
            { title: "Compartirlo con el interesado", action: "Enviá el enlace antes de que la persona cree su cuenta. Acompañalo con una explicación del producto y del proceso de alta.", result: "El nuevo registro entra por el circuito correcto." },
            { title: "Completar registro y verificación", action: "La persona debe registrarse y verificar su correo desde ese flujo. El afiliado no necesita conocer su contraseña.", result: "La cuenta queda asociada al referente sin compartir credenciales." },
            { title: "Crear el proyecto del cliente", action: "El nuevo usuario crea o recibe su proyecto y selecciona los servicios que necesita.", result: "La red ya contiene una cuenta con actividad comercial identificable." },
            { title: "Esperar una mensualidad válida", action: "El registro o la prueba no generan una comisión confirmada. El cálculo ocurre cuando una mensualidad elegible queda aprobada.", result: "La ganancia nace de un servicio real y cobrado." },
          ]}
          verification={["La cuenta figura dentro de la red del afiliado.", "El proyecto asociado muestra plan, módulos y mensualidad.", "No aparece comisión confirmada antes de un pago válido."]}
          screenshot="enlace personal y tabla de cuentas referidas"
        />

        <TechnicalGuide
          title="2. Comprender el reparto dinámico de la red"
          summary="La bolsa total puede cambiar con el tiempo sin modificar usuario por usuario; cada pago conserva una fotografía de la política aplicada."
          steps={[
            { title: "Definir la bolsa de la red", action: "WebProfit determina qué porcentaje máximo de cada mensualidad elegible se destina a toda la cadena de afiliados. El resto queda para WebProfit.", result: "La suma distribuida nunca supera el límite global." },
            { title: "Aplicar la reducción por profundidad", action: "La persona que originó al cliente recibe el derecho correspondiente a su posición; los referentes superiores reciben diferencias menores según la curva vigente.", result: "La comisión se reparte hacia arriba sin sumar porcentajes ilimitados." },
            { title: "Usar la política vigente por fecha", action: "Cuando WebProfit cambia margen o reducción, crea una nueva política desde una fecha determinada. Los usuarios no necesitan reconfigurarse.", result: "Los pagos futuros usan las nuevas reglas y los anteriores conservan las viejas." },
            { title: "Guardar el cálculo del movimiento", action: "Cada comisión conserva pago de origen, proyecto, beneficiario, distancia, porcentaje real, importe, moneda y versión de política.", result: "El movimiento puede auditarse sin recalcular la historia." },
            { title: "Cancelar cuando se revierte el pago", action: "Si una mensualidad se devuelve, cancela o sufre contracargo, la comisión relacionada debe quedar cancelada, no eliminada.", result: "La red no cobra sobre dinero que WebProfit no conservó." },
          ]}
          statusFlow={["Pago aprobado", "Comisión disponible", "Comisión pagada"]}
          verification={["La suma de beneficiarios no supera la bolsa global.", "Un pago repetido no crea la misma comisión dos veces.", "Cada movimiento muestra la política usada.", "Un reembolso conserva el historial y cancela la ganancia."]}
          warnings={["El porcentaje mostrado al afiliado corresponde a la comisión sobre una mensualidad elegible, no necesariamente al total que paga el cliente si existen conceptos no comisionables."]}
          screenshot="detalle de una comisión directa y otra generada por la red"
        />

        <TechnicalGuide
          title="3. Leer el panel del afiliado"
          summary="El panel se presenta desde la perspectiva del usuario, sin revelar información privada de ramas ajenas."
          steps={[
            { title: "Revisar referidos directos", action: "Son las personas registradas mediante el enlace del afiliado. Verificá cuenta, proyecto y situación comercial.", result: "El afiliado conoce a quién incorporó personalmente." },
            { title: "Revisar red ampliada", action: "Incluye descendientes generados por referidos. La profundidad sirve para explicar el origen de una comisión de expansión.", result: "Se entiende cómo crece la red sin cambiar el vínculo directo." },
            { title: "Consultar proyectos", action: "Observá negocio, módulos, mensualidad, estado y próxima fecha cuando esté disponible. No confundas mensualidad estimada con pago aprobado.", result: "La expectativa de comisión se basa en proyectos reales." },
            { title: "Separar disponible, pagado y cancelado", action: "Disponible significa generado y pendiente de liquidación; pagado confirma la entrega al afiliado; cancelado indica reversión del pago de origen.", result: "El saldo se interpreta correctamente." },
            { title: "Reportar una diferencia", action: "Enviá a soporte afiliado, proyecto, fecha de pago y movimiento. No compartas datos personales innecesarios de clientes.", result: "La revisión respeta privacidad y trazabilidad." },
          ]}
          verification={["Los totales coinciden con la suma de movimientos visibles.", "Los proyectos de red muestran sólo información comercial necesaria.", "Cada comisión puede relacionarse con una mensualidad concreta."]}
          screenshot="resumen de red, proyectos referidos y movimientos de comisión"
        />
      </DocumentationSection>

      <DocumentationSection id="vendedores" shaded>
        <SectionHeading
          eyebrow="Proceso comercial"
          title="Llevar al cliente desde el diagnóstico hasta la operación"
          description="La responsabilidad del vendedor no termina al crear la cuenta. Una venta completa incluye relevamiento, propuesta, alta, carga mínima, prueba, capacitación y seguimiento."
        />

        <TechnicalGuide
          defaultExpanded
          title="1. Relevar el negocio antes de cotizar"
          summary="El plan se arma sobre el circuito real del cliente, no sobre una lista genérica de funciones."
          steps={[
            { title: "Entender qué vende", action: "Identificá productos, servicios o ambos; cantidad aproximada de publicaciones; categorías; stock; precios; zonas y modalidades.", result: "Se define la base de catálogo." },
            { title: "Entender cómo recibe clientes", action: "Preguntá si vende por WhatsApp, local, redes, reparto, retiro, turnos o e-commerce. Registrá el problema principal del proceso actual.", result: "Se prioriza la herramienta que resuelve una necesidad concreta." },
            { title: "Entender cobros", action: "Relevá medios de pago, cuenta de Mercado Pago, necesidad de seña, pago total, coordinación o cobro presencial.", result: "La propuesta no promete un checkout incompatible." },
            { title: "Entender equipo y administración", action: "Definí cuántas personas usarán el sistema, qué roles necesitan y si deben controlar caja, stock o movimientos.", result: "Se consideran accesos y módulo de gestión." },
            { title: "Reunir material", action: "Solicitá logo, colores, descripción, horarios, contacto, redes, fotos y listado inicial de oferta.", result: "La puesta en marcha no se detiene después de contratar." },
          ]}
          verification={["Existe una lista concreta de necesidades y no sólo interés general.", "Cada módulo propuesto responde a un uso identificado.", "El vendedor conoce quién entregará el material y en qué fecha."]}
          screenshot="ficha de relevamiento comercial completada"
        />

        <TechnicalGuide
          title="2. Presentar, activar y entregar el proyecto"
          summary="La demostración debe seguir el recorrido del cliente final y luego mostrar el panel administrativo."
          steps={[
            { title: "Mostrar el resultado", action: "Empezá por la página pública, catálogo, reserva o compra. Después mostrá cómo se administra desde el panel.", result: "El cliente entiende el beneficio antes de la complejidad." },
            { title: "Definir alcance", action: "Enumerá módulos incluidos, tareas de carga, acompañamiento, mensualidad total y qué queda a cargo del cliente.", result: "No existen expectativas ocultas." },
            { title: "Registrar con el enlace correcto", action: "Cuando corresponda, utilizá el enlace de referido del vendedor antes de crear la cuenta.", result: "La atribución queda registrada desde el inicio." },
            { title: "Crear y personalizar", action: "Acompañá la creación del proyecto, identidad, página y primeras publicaciones. No abandones el proyecto con contenido de ejemplo.", result: "El cliente recibe una base real y reconocible." },
            { title: "Configurar el módulo principal", action: "Completá al menos una operación de punta a punta: pedido, pago, reserva o movimiento, según el caso.", result: "La herramienta principal fue probada, no sólo activada." },
            { title: "Capacitar con roles reales", action: "Mostrá al propietario y a cada responsable únicamente las tareas que realizará. Entregá accesos individuales.", result: "El equipo puede operar sin depender del vendedor para cada paso." },
            { title: "Cerrar con checklist", action: "Revisá dirección pública, contacto, módulos, mensualidad, pago, accesos, prueba operativa y canal de soporte.", result: "La entrega tiene criterios objetivos de finalización." },
          ]}
          verification={["El cliente puede ingresar sin ayuda.", "La página no contiene datos de demostración.", "Se completó una operación de prueba realista.", "El cliente conoce su mensualidad total y próxima acción."]}
          screenshot="proyecto terminado y checklist de entrega firmado o confirmado"
        />

        <TechnicalGuide
          title="3. Seguimiento de los primeros 30 días"
          summary="El objetivo es transformar una activación en uso habitual antes de que finalice la prueba o llegue la primera renovación."
          steps={[
            { title: "Primeras 24 horas", action: "Confirmá acceso, dirección pública, material faltante y responsable operativo.", result: "No quedan bloqueos básicos de inicio." },
            { title: "Primera semana", action: "Revisá cantidad de publicaciones, pruebas realizadas, consultas o reservas y dudas del equipo.", result: "El proyecto comienza a usarse con datos reales." },
            { title: "Mitad del período", action: "Detectá módulos sin usar, configuraciones incompletas y oportunidades de simplificar el proceso.", result: "La solución se adapta antes de la decisión de continuidad." },
            { title: "Antes del vencimiento", action: "Repasá valor obtenido, módulos necesarios, mensualidad definitiva y modalidad de pago.", result: "La renovación no llega como una sorpresa." },
            { title: "Después de activar", action: "Confirmá el primer pago, el acceso completo y la continuidad de página, pedidos, agenda o sistema.", result: "La cuenta pasa de prueba a operación estable." },
          ]}
          verification={["El proyecto tiene actividad real antes del vencimiento.", "El cliente sabe qué módulos conservará.", "El vendedor registra próximos pasos y fecha de seguimiento."]}
          screenshot="resumen del proyecto antes de finalizar la prueba"
        />
      </DocumentationSection>

      <DocumentationSection id="control">
        <SectionHeading
          eyebrow="Control y diagnóstico"
          title="Revisar el sistema sin exponer información técnica"
          description="Cuando algo falla, la mejor evidencia es una combinación de proyecto, usuario, fecha, número de operación, estado y captura del mensaje visible. Contraseñas, credenciales y datos privados nunca deben enviarse."
        />

        <Grid container spacing={2.2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <FeatureCard icon={CheckCircleRoundedIcon} title="Control diario" description="Pedidos y pagos pendientes, turnos del día, caja abierta, stock crítico y notificaciones fallidas." />
          </Grid>
          <Grid item xs={12} md={4}>
            <FeatureCard icon={TrendingUpRoundedIcon} title="Control semanal" description="Uso de módulos, publicaciones incompletas, diferencias de stock, membresías y rendimiento comercial." />
          </Grid>
          <Grid item xs={12} md={4}>
            <FeatureCard icon={SettingsRoundedIcon} title="Control mensual" description="Suscripción, próxima fecha, servicios activos, comisiones, cierres y continuidad de accesos." />
          </Grid>
        </Grid>

        <TechnicalGuide
          defaultExpanded
          title="Checklist técnico antes de publicar"
          summary="Este control evita que una página visualmente terminada falle en su primera interacción real."
          steps={[
            { title: "Acceso", action: "Probá propietario y al menos un miembro con su propio usuario.", result: "Cada persona ve el proyecto y las funciones correctas." },
            { title: "Página", action: "Revisá logo, textos, botones, contacto, enlaces, visibilidad, SEO y vista móvil.", result: "No hay datos de ejemplo ni enlaces rotos." },
            { title: "Catálogo", action: "Abrí categorías, búsquedas, fichas, imágenes, precios y estados activos.", result: "La oferta pública coincide con el panel." },
            { title: "Pedido", action: "Agregá un producto, completá datos, elegí entrega y generá un pedido de prueba.", result: "El pedido llega una vez y reserva stock correctamente." },
            { title: "Pago", action: "Cuando esté habilitado, completá una prueba controlada y verificá el estado desde el panel y el proveedor.", result: "El cobro y el pedido quedan relacionados." },
            { title: "Agenda", action: "Reservá un horario, revisá capacidad, buffers, estado y pago.", result: "No se ofrecen horarios inválidos." },
            { title: "Gestión", action: "Abrí y cerrá una caja de prueba, registrá un movimiento y verificá sus cuentas relacionadas.", result: "La operación se refleja sin duplicados." },
            { title: "Suscripción", action: "Confirmá módulos, mensualidad, estado y próxima fecha.", result: "El acceso coincide con lo contratado." },
          ]}
          verification={["Todas las pruebas se realizaron desde la dirección pública y desde el panel.", "Los datos de prueba se identificaron y limpiaron o conservaron como prueba documentada.", "El propietario recibió el enlace, acceso y canal de soporte."]}
          screenshot="checklist completo con cada punto marcado"
        />

        <TechnicalGuide
          title="Datos necesarios para solicitar soporte"
          summary="Una solicitud completa reduce tiempos de diagnóstico y evita pedir acceso innecesario a la cuenta."
          steps={[
            { title: "Identificar el proyecto", action: "Indicá nombre del negocio y dirección pública o proyecto seleccionado.", result: "Se revisa el entorno correcto." },
            { title: "Describir el recorrido", action: "Explicá qué pantalla abriste, qué acción realizaste y qué esperabas que ocurriera.", result: "El problema puede reproducirse." },
            { title: "Indicar fecha y hora", action: "Agregá momento aproximado y zona horaria, especialmente para pagos, turnos y cierres.", result: "La operación puede localizarse temporalmente." },
            { title: "Agregar referencia", action: "Incluí número de pedido, turno, movimiento, pago o correo del usuario cuando sea necesario.", result: "Se evita revisar datos no relacionados." },
            { title: "Adjuntar captura segura", action: "Mostrá mensaje y contexto, ocultando documentos, contraseñas, tokens, tarjetas y datos privados.", result: "Soporte recibe evidencia sin exponer seguridad." },
            { title: "Indicar alcance", action: "Aclarar si ocurre siempre, con un usuario, un producto, un dispositivo o un navegador concreto.", result: "Se distingue una falla general de una configuración puntual." },
          ]}
          verification={["La solicitud no contiene contraseñas.", "El proyecto y la operación están identificados.", "La captura incluye el mensaje completo y no sólo una parte recortada."]}
          screenshot="ejemplo de solicitud de soporte bien documentada"
        />
      </DocumentationSection>

      <DocumentationSection id="preguntas" shaded>
        <SectionHeading
          eyebrow="Ayuda rápida"
          title="Preguntas frecuentes"
          description="Estas respuestas sirven como referencia general. Para una configuración particular, revisá primero el proceso correspondiente y luego contactá a soporte con los datos del proyecto."
        />

        <Box sx={{ maxWidth: 920 }}>
          {faqs.map((faq) => (
            <Accordion
              key={faq.question}
              disableGutters
              elevation={0}
              sx={{
                mb: 1.2,
                border: "1px solid rgba(25,36,47,.1)",
                borderRadius: "16px !important",
                overflow: "hidden",
                bgcolor: "#fff",
                "&::before": { display: "none" },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ px: 2.4, py: 0.5 }}>
                <Typography sx={{ color: BRAND.dark, fontWeight: 900 }}>{faq.question}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2.4, pt: 0, pb: 2.4 }}>
                <Typography sx={{ color: "rgba(25,36,47,.66)", lineHeight: 1.7 }}>{faq.answer}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        <Paper
          elevation={0}
          sx={{
            mt: 4,
            p: { xs: 3, md: 4.5 },
            borderRadius: 5,
            color: "#fff",
            background: "linear-gradient(135deg, #19242F 0%, #243746 60%, #F35A3A 175%)",
          }}
        >
          <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems={{ xs: "flex-start", md: "center" }} justifyContent="space-between">
            <Box sx={{ maxWidth: 680 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <SupportAgentRoundedIcon sx={{ color: "#FFB49F" }} />
                <Typography sx={{ color: "#FFB49F", fontWeight: 900, textTransform: "uppercase", letterSpacing: ".09em", fontSize: ".76rem" }}>
                  Acompañamiento WebProfit
                </Typography>
              </Stack>
              <Typography sx={{ mt: 1.2, fontWeight: 950, fontSize: { xs: "1.7rem", md: "2.25rem" }, lineHeight: 1.15 }}>
                ¿Necesitás ayuda para terminar un proyecto?
              </Typography>
              <Typography sx={{ mt: 1.2, color: "rgba(255,255,255,.72)", lineHeight: 1.7 }}>
                Enviá nombre del negocio, proyecto, paso, fecha, referencia de la operación y una captura segura del mensaje visible.
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="large"
              startIcon={<WhatsAppIcon />}
              onClick={() => {
                window.location.href = WHATSAPP_URL;
              }}
              sx={{
                minHeight: 52,
                px: 2.8,
                borderRadius: 999,
                bgcolor: BRAND.orange,
                textTransform: "none",
                fontWeight: 900,
                "&:hover": { bgcolor: BRAND.orangeDark },
              }}
            >
              Contactar soporte
            </Button>
          </Stack>
        </Paper>
      </DocumentationSection>
    </Box>
  );
}
